// ────────────────────────────────────────────────────────────────────────────
// BÉNÉFICE / MARGE — SOURCE UNIQUE DE VÉRITÉ POUR TOUT L'ERP
//
// Toute somme d'argent liée au bénéfice (ventes, caisse, commissions
// représentants, commissions pro) DOIT passer par ces fonctions. Ne JAMAIS
// recopier la formule ailleurs.
//
// Règle métier (validée avec Khalil) :
//
//   bénéfice ligne = (prixVenteNetHT − coûtUnitaireNetHT) × quantité
//
//   • prixVenteNetHT     = unitPriceHt × (1 − discountPct/100)
//                          (prix de vente HT après remise commerciale)
//   • coûtUnitaireNetHT  = purchasePriceHt × (1 − supplierDiscount/100)
//                          (prix d'achat HT NET de la remise fournisseur)
//
// 1. COÛT NET — on déduit toujours la remise fournisseur. C'est le coût réel
//    d'acquisition, identique à celui affiché sur la fiche article.
// 2. SNAPSHOT — le coût net est figé sur la ligne au moment de la vente. Le
//    bénéfice d'un document enregistré ne change jamais si le prix d'achat de
//    l'article évolue ensuite. Les appelants passent le coût figé via
//    `unitCostHt` ; le catalogue (`purchasePriceHt` + `supplierDiscount`) n'est
//    qu'un repli quand aucun snapshot n'existe.
// 3. COÛT INCONNU — une ligne sans coût connu (≤ 0) est EXCLUE du total et
//    signalée via `hasKnownPurchasePrice`. On ne compte JAMAIS une vente comme
//    100 % de bénéfice faute de coût (sinon on surévalue le bénéfice et les
//    commissions — risque financier).
// ────────────────────────────────────────────────────────────────────────────

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(n, 0), 100);
}

/** Champs catalogue nécessaires pour dériver le coût net d'un article. */
export interface ArticleCostInput {
  /** Prix d'achat brut HT. */
  purchasePriceHt?: number | null;
  /** Remise fournisseur en % (0–100). */
  supplierDiscount?: number | null;
}

/**
 * Coût unitaire NET HT = prix d'achat brut HT × (1 − remise fournisseur/100).
 *
 * Retourne `null` si le prix d'achat brut est inconnu (≤ 0) : on ne devine
 * jamais un coût. C'est l'unique endroit où la remise fournisseur est appliquée.
 */
export function netUnitCostHt(article: ArticleCostInput | null | undefined): number | null {
  const gross = Number(article?.purchasePriceHt);
  if (!Number.isFinite(gross) || gross <= 0) return null;
  const discount = clampPct(Number(article?.supplierDiscount) || 0);
  return round3(gross * (1 - discount / 100));
}

export interface SalesBenefitLineInput {
  /** Optionnel — conservé pour compat ; le calcul ne dépend que du coût. */
  articleId?: string | null;
  quantity: number;
  unitPriceHt: number;
  discountPct?: number | null;
  /**
   * Coût unitaire net HT figé sur la ligne (snapshot). PRIORITAIRE quand > 0.
   */
  unitCostHt?: number | null;
  /** Repli catalogue (si pas de snapshot) : prix d'achat brut HT. */
  purchasePriceHt?: number | null;
  /** Repli catalogue : remise fournisseur en % (appliquée au prix d'achat brut). */
  supplierDiscount?: number | null;
}

export interface SalesLineBenefit {
  benefit: number;
  /** Coût unitaire net HT retenu, ou `null` si inconnu. */
  unitCostHt: number | null;
  /** `true` seulement si un coût net a pu être déterminé. */
  hasKnownPurchasePrice: boolean;
}

export interface SalesDocumentBenefit {
  totalBenefit: number;
  hasKnownPurchasePrice: boolean;
}

/**
 * Coût unitaire net retenu pour une ligne : le snapshot figé d'abord, sinon
 * le coût net dérivé du catalogue.
 */
export function resolveLineNetCost(line: SalesBenefitLineInput): number | null {
  const snapshot = Number(line.unitCostHt);
  if (Number.isFinite(snapshot) && snapshot > 0) return round3(snapshot);
  return netUnitCostHt({
    purchasePriceHt: line.purchasePriceHt,
    supplierDiscount: line.supplierDiscount,
  });
}

export function calculateSalesLineBenefit(line: SalesBenefitLineInput): SalesLineBenefit {
  const unitCostHt = resolveLineNetCost(line);
  if (unitCostHt == null) {
    return { benefit: 0, unitCostHt: null, hasKnownPurchasePrice: false };
  }

  const quantity = Number(line.quantity) || 0;
  const unitPriceHt = Number(line.unitPriceHt) || 0;
  const discountPct = clampPct(Number(line.discountPct) || 0);
  const sellPriceNetHt = unitPriceHt * (1 - discountPct / 100);
  const benefit = round3((sellPriceNetHt - unitCostHt) * quantity);

  return { benefit, unitCostHt, hasKnownPurchasePrice: true };
}

export function calculateSalesDocumentBenefit(lines: SalesBenefitLineInput[]): SalesDocumentBenefit {
  let totalBenefit = 0;
  let hasKnownPurchasePrice = false;

  for (const line of lines) {
    const lineBenefit = calculateSalesLineBenefit(line);
    if (!lineBenefit.hasKnownPurchasePrice) continue;
    totalBenefit += lineBenefit.benefit;
    hasKnownPurchasePrice = true;
  }

  return {
    totalBenefit: round3(totalBenefit),
    hasKnownPurchasePrice,
  };
}
